import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { memo, useCallback, useState } from "react";

export interface TagsFilterProps {
  /** All available tags from GraphQL */
  allFacilityTags: Array<{ id: string; name: string }>;
  /** Current committed tag names from omnisearch */
  committedFacilityTagNames: string[];
  /** Whether the tags filter shows "No Tags" state */
  isFacilityTagsNull: boolean;
  /** Loading state for pagination */
  isFetchingMoreTags: boolean;
  /** Called when dropdown closes with selected tags */
  onCommit: (tagNames: string[]) => void;
  /** Called when user scrolls for pagination */
  onScroll: (event: React.UIEvent<HTMLDivElement>) => void;
}

/**
 * Memoized Tags filter component that manages its own menu anchor state.
 * This prevents parent re-renders when the dropdown opens/closes.
 */
export const TagsFilter = memo(function TagsFilter({
  allFacilityTags,
  committedFacilityTagNames,
  isFacilityTagsNull,
  isFetchingMoreTags,
  onCommit,
  onScroll,
}: TagsFilterProps) {
  // === LOCAL STATE - changes here don't re-render parent ===
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(menuAnchor);
  const [intermediateTagNames, setIntermediateTagNames] = useState<string[]>([]);

  // Display intermediate when open, committed when closed
  const selectedFacilityTagNames = open ? intermediateTagNames : committedFacilityTagNames;

  // Check if at default (none selected)
  const isAtDefault = selectedFacilityTagNames.length === 0;

  // Initialize intermediate state when dropdown opens
  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setIntermediateTagNames([...committedFacilityTagNames]);
      setMenuAnchor(event.currentTarget);
    },
    [committedFacilityTagNames]
  );

  // Commit changes when dropdown closes
  const handleClose = useCallback(() => {
    setMenuAnchor(null);
    onCommit(intermediateTagNames);
  }, [intermediateTagNames, onCommit]);

  // Toggle individual tag selection
  const handleTagToggle = useCallback((tagName: string) => {
    setIntermediateTagNames((prev) => {
      const isSelected = prev.some((name) => name.toLowerCase() === tagName.toLowerCase());
      if (isSelected) {
        return prev.filter((name) => name.toLowerCase() !== tagName.toLowerCase());
      } else {
        return [...prev, tagName];
      }
    });
  }, []);

  // Toggle all tags handler
  const handleToggleAll = useCallback(() => {
    if (intermediateTagNames.length === allFacilityTags.length) {
      setIntermediateTagNames([]);
    } else {
      setIntermediateTagNames(allFacilityTags.map((t) => t.name));
    }
  }, [intermediateTagNames.length, allFacilityTags]);

  // Reset tags to default (none selected)
  const handleReset = useCallback(() => {
    setIntermediateTagNames([]);
  }, []);

  return (
    <Box>
      <Chip
        label={
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <span>
              {isFacilityTagsNull
                ? "No Tags"
                : selectedFacilityTagNames.length > 0
                  ? `Tags (${selectedFacilityTagNames.length})`
                  : "Tags"}
            </span>
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Stack>
        }
        color={open ? "primary" : "default"}
        variant="filled"
        onClick={handleOpen}
        clickable
        size="medium"
      />
      <Menu
        anchorEl={menuAnchor}
        open={open}
        onClose={handleClose}
        // Prevent menu from affecting body scroll, which triggers DataGrid's ResizeObserver
        disableScrollLock
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: { maxHeight: 350, mt: 0.5, minWidth: 280 },
            onScroll: onScroll,
          },
        }}
      >
        <Box
          sx={{
            pl: 2,
            pr: 1,
            py: 0.75,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  selectedFacilityTagNames.length === allFacilityTags.length &&
                  allFacilityTags.length > 0
                }
                indeterminate={
                  selectedFacilityTagNames.length > 0 &&
                  selectedFacilityTagNames.length < allFacilityTags.length
                }
                onChange={handleToggleAll}
                disabled={allFacilityTags.length === 0}
              />
            }
            label="Show/Hide All"
            sx={{ margin: 0 }}
          />
          {!isAtDefault && <Button onClick={handleReset}>Reset</Button>}
        </Box>
        {allFacilityTags.length === 0 && <MenuItem disabled>No tags available</MenuItem>}
        {allFacilityTags.map((tag) => (
          <MenuItem key={tag.id} value={tag.name} onClick={() => handleTagToggle(tag.name)}>
            <Checkbox
              checked={selectedFacilityTagNames.some(
                (name) => name.toLowerCase() === tag.name.toLowerCase()
              )}
            />
            <Tooltip title={tag.name} placement="right">
              <Typography
                noWrap
                sx={{
                  maxWidth: 160,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {tag.name}
              </Typography>
            </Tooltip>
          </MenuItem>
        ))}
        {isFetchingMoreTags && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 1,
            }}
          >
            <CircularProgress size={20} />
          </Box>
        )}
      </Menu>
    </Box>
  );
});
